import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Checkbox, Stack, Link, Box,
} from "@chakra-ui/react";

type Props = {
  version?: string;
  storageKey?: string;
  ttlDays?: number;
  forceOpen?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
};

type Stored = {
  dismissedAt: number;
  version: string;
  dontShowAgain: boolean;
};

export const PopupNudge: React.FC<Props> = ({
  version = "v1",
  storageKey = "popup-nudge",
  ttlDays,
  forceOpen = false,
  size = "lg",
}) => {
  const lsKey = useMemo(() => `${storageKey}:${version}`, [storageKey, version]);
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      return;
    }
    const raw = typeof window !== "undefined" ? localStorage.getItem(lsKey) : null;
    if (!raw) {
      setIsOpen(true);
      return;
    }
    try {
      const data = JSON.parse(raw) as Stored;
      if (data.version !== version) return setIsOpen(true);
      if (ttlDays && Date.now() - data.dismissedAt > ttlDays * 86400000)
        return setIsOpen(true);
      setIsOpen(!data.dontShowAgain);
    } catch {
      setIsOpen(true);
    }
  }, [lsKey, version, ttlDays, forceOpen]);

  const persistClose = () => {
    const payload: Stored = {
      dismissedAt: Date.now(),
      version,
      dontShowAgain,
    };
    localStorage.setItem(lsKey, JSON.stringify(payload));
    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={persistClose} isCentered size={size} motionPreset="scale">
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent>
        <ModalHeader>Pop-ups might be blocked</ModalHeader>
        <ModalBody>
          <Box>
            <Box>
              <div>
                Our sign-in uses a browser pop-up. If pop-ups are blocked, login may fail.
                Please allow pop-ups for this site in your browser settings.
                <Box mt={3} fontSize="sm" opacity={0.95}>
                  <Stack spacing={2}>
                    <span>See how to allow pop-ups in:</span>
                    <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                      <Link href="https://support.microsoft.com/en-us/microsoft-edge/block-pop-ups-in-microsoft-edge-1d8ba4f8-f385-9a0b-e944-aa47339b6bb5" isExternal>Microsoft Edge</Link>
                      <Link href="https://support.google.com/chrome/answer/95472?hl=en&co=GENIE.Platform%3DDesktop" isExternal>Google Chrome</Link>
                      <Link href="https://support.mozilla.org/en-US/kb/pop-blocker-settings-exceptions-troubleshooting" isExternal>Mozilla Firefox</Link>
                      <Link href="https://support.apple.com/en-gb/guide/safari/sfri40696/mac" isExternal>Apple Safari</Link>
                    </Stack>
                  </Stack>
                </Box>
                <Checkbox
                  mt={3}
                  isChecked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                >
                  Don’t show this again
                </Checkbox>
              </div>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <button type="button" className="el-btn" onClick={persistClose}>
            Got it
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
